import { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { Card } from "./common";
import { ChartBar, Vault } from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from "recharts";
import { EST_STAGES } from "../lib/constants";

const COLORS = ["#FF5A00", "#0EA5E9", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#64748B", "#14B8A6"];
const compact = (n) => new Intl.NumberFormat("es-MX", { notation: "compact", maximumFractionDigits: 1 }).format(n || 0);

function KItem({ label, value, tone = "" }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-heading font-extrabold text-xl mt-0.5 ${tone}`}>{value}</div>
    </div>
  );
}

export function FinancialCharts() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/financial").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || !d.per_contract) return null;

  const bars = d.per_contract.slice(0, 8).map((c) => ({
    name: c.title.length > 14 ? c.title.slice(0, 14) + "…" : c.title,
    Contratado: c.contracted, Pagado: c.paid, "Por pagar": c.por_pagar,
  }));
  const pie = (d.status_dist || []).map((s) => ({ name: (EST_STAGES[s.status] || {}).short || s.status, value: s.count }));
  const area = (d.monthly_paid || []).map((m) => ({ month: m.month, Pagado: m.paid }));
  const t = d.totals || {};

  return (
    <Card className="mt-6 p-6" data-testid="financial-charts">
      <div className="flex items-center gap-2 mb-4">
        <ChartBar size={20} weight="fill" className="text-primary" />
        <div className="font-heading font-bold text-xl">Panel financiero</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 pb-6 border-b border-border">
        <KItem label="Contratado" value={money(t.contracted)} />
        <KItem label="Pagado" value={money(t.paid)} tone="text-success" />
        <KItem label="Por pagar" value={money(t.por_pagar)} tone="text-primary" />
        <div className="flex items-start gap-1.5">
          <Vault size={18} weight="bold" className="text-amber-600 mt-1" />
          <KItem label="Fondo de garantía" value={money(t.retenido)} tone="text-amber-600" />
        </div>
        <KItem label="Saldo por ejercer" value={money((t.contracted || 0) - (t.gross || 0))} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="overline mb-3">Contratado vs. pagado por contrato</div>
          {bars.length === 0 ? <p className="text-sm text-muted-foreground py-8">Sin datos.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={bars} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Contratado" fill="#0F172A" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Pagado" fill="#10B981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Por pagar" fill="#FF5A00" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div>
          <div className="overline mb-3">Estimaciones por etapa</div>
          {pie.length === 0 ? <p className="text-sm text-muted-foreground py-8">Sin estimaciones.</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={70} label={(e) => e.value}>
                  {pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {area.length > 0 && (
        <div className="mt-6">
          <div className="overline mb-3">Pagos aplicados por mes</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={area} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF5A00" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#FF5A00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
              <Area type="monotone" dataKey="Pagado" stroke="#FF5A00" strokeWidth={2} fill="url(#gPaid)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
